import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Sprout } from "lucide-react";
import { CULTIVATION_TYPE, HarvestSeasonData } from "../../../../config/constants";
import { useForm } from "react-hook-form";
import { validateCultivationNumber } from "../../../../utils/utils";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import CultivarSaveReminder from "./CultivarSaveReminder";
import { useCultivarSectionAccordion } from "../hooks/useCultivarSectionAccordion";

const Fields = forwardRef((
  {
    currentDataType,
    currentFormData,
    disabled = false,
    sectionsDisabled = false,
    enableAccodationStatus = false,
    onValidityChange = () => {}
  }: any,
  ref
) => {
  const [harvestSeason, setHarvestSeason] = useState<string>("Unknown")

  const values = useMemo(() => {
    setHarvestSeason(currentFormData?.cultivationField?.harvestSeason)
    return {
      yield: currentFormData?.cultivationField?.yield,
      harvestYear: currentFormData?.cultivationField?.harvestYear,
      growthArea: currentFormData?.cultivationField?.growthArea,
      harvestSeason: currentFormData?.cultivationField?.harvestSeason ?? "",
    }
  }, [currentFormData])

  const form = useForm({
    defaultValues: values,
    values: values,
    mode: "all",
    reValidateMode: "onChange",
    shouldUnregister: false
  });

  const {
    formState: { errors, isValid },
    control,
    trigger,
    getValues
  } = form;

  const { accordionEnabled, toggleAccordion, headerClassName } = useCultivarSectionAccordion(
    sectionsDisabled,
    enableAccodationStatus,
    Object.keys(errors).length > 0,
    true
  );

  const handleClick = toggleAccordion;

  useImperativeHandle(ref, () => ({
    getFieldData: async () => {
      const isFormValid = await trigger();

      if (!isFormValid) {
        return null;
      }

      let fieldData = getValues();
      fieldData.harvestSeason = harvestSeason
      return fieldData
    }
  }));

  useEffect(() => {
    void trigger();
  }, [values, trigger]);

  useEffect(() => {
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  return (
    <Card className="tw-border-border tw-p-0 tw-gap-0">
      <CardHeader className={headerClassName} onClick={handleClick}>
        <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Sprout className="tw-h-4 tw-w-4 tw-text-primary" />
            <span className="tw-text-sm tw-font-semibold tw-text-foreground">Field</span>
          </div>
          {accordionEnabled ? <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" /> : <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />}
        </div>
      </CardHeader>
      <CardContent
        className={`tw-py-3.5 tw-px-3.5 tw-space-y-4 ${accordionEnabled ? "" : "tw-hidden"}`}
      >
        <Form {...form}>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
            {currentDataType === CULTIVATION_TYPE.Supplier ? (
                <FormInputNumber
                  control={control}
                  onChangeFunction={() => trigger("yield")}
                  label="Yield (kg/ha)"
                  placeholder="Yield (kg/ha)"
                  name="yield"
                  required
                  disabled={disabled}
                  rules={{
                    validate: {
                      valueCheck: (v: any) => validateCultivationNumber(v)
                    }
                  }}
                />
            ) : null}
              <FormInputNumber
                label="Harvest year"
                placeholder="Harvest year"
                control={control}
                name="harvestYear"
                isYearInput={true}
                disabled={disabled}
                onChangeFunction={() => trigger("harvestYear")}
              />
            {currentDataType === CULTIVATION_TYPE.Supplier ? (
                <FormInputNumber
                  control={control}
                  label="Growth Area (ha)"
                  placeholder="Growth Area (ha)"
                  name="growthArea"
                  required
                  disabled={disabled}
                  rules={{
                    validate: {
                      valueCheck: (v: any) => validateCultivationNumber(v)
                    }
                  }}
                />
            ) : null}
              <FormSelect
                control={control}
                name="harvestSeason"
                label="Harvest season"
                placeholder="Select Season"
                options={HarvestSeasonData}
                onChange={setHarvestSeason}
                disabled={disabled}
              />
            </div>
          </Form>
          <CultivarSaveReminder section="field" hidden={disabled} />
      </CardContent>
    </Card>
  );
})

export default Fields;
